/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Client-side Builder.io A/B variant resolution.
 *
 * Why the client and not the server: behind CloudFront the origin cannot read a
 * stable per-visitor identifier. CloudFront strips the `builder.tests` Set-Cookie
 * on the way out and does not reliably forward it back in, and the forwarded
 * client IP the origin sees rotates request-to-request, so server-side bucketing
 * (resolveAbVariant + abBucketSeed) re-buckets the visitor on almost every
 * navigation. That is the "I keep seeing different variants clicking around" bug.
 *
 * A first-party id we set from JS (document.cookie) is untouched by CloudFront and
 * survives across visits, so it is the only stable seed available. We read/create
 * it here, then pick the variant with the SAME weighted-pick math the server used
 * (mirrors the SDK's processResultsForTests: accumulate each variation's
 * testRatio, remainder falls to the parent). Same visitor -> same variant forever.
 *
 * The server now renders the parent (default) variant and ships every variation's
 * data to the client; this module chooses which one the visitor actually sees.
 */

const VISITOR_COOKIE = 'ab_visitor';
const VISITOR_COOKIE_DAYS = 365;
const TEST_COOKIE_PREFIX = 'builder.tests';

export type BuilderVariationMap = Record<string, { id?: string; testRatio?: number; data?: any }>;

/**
 * FNV-1a hash of `str` normalized to [0, 1). Deterministic and uniform. Must stay
 * byte-identical to the vanilla-JS copy in the anti-flicker snippet (see
 * StickyAbVariant.tsx) so the pre-paint script and this hook pick the same variant.
 */
const hashStringToUnit = (str: string): number => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.codePointAt(i) ?? 0;
    h = Math.imul(h, 16777619);
  }

  return (h >>> 0) / 4294967296;
};

const readVisitorCookie = (): string => {
  const match = new RegExp(`(?:^|; )${VISITOR_COOKIE}=([^;]+)`).exec(document.cookie);

  return match ? decodeURIComponent(match[1]) : '';
};

const makeVisitorId = (): string => {
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c?.randomUUID) {
    return c.randomUUID();
  }
  if (c?.getRandomValues) {
    const buf = new Uint32Array(4);
    c.getRandomValues(buf);

    return Array.from(buf, n => n.toString(16)).join('');
  }

  // Last resort; the persisted cookie still makes it stable per visitor afterwards.
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;
};

/**
 * Stable per-visitor id, persisted in a first-party cookie so the same browser
 * keeps it across visits. Returns '' on the server (no document), where callers
 * fall back to the parent variant.
 */
export const getOrCreateVisitorId = (): string => {
  if (typeof document === 'undefined') {
    return '';
  }
  const existing = readVisitorCookie();
  if (existing) {
    return existing;
  }
  const id = makeVisitorId();
  document.cookie = `${VISITOR_COOKIE}=${encodeURIComponent(id)}; Path=/; Max-Age=${
    VISITOR_COOKIE_DAYS * 24 * 60 * 60
  }; SameSite=Lax`;

  return id;
};

/**
 * Deterministic weighted pick over the variations, mirroring the Builder SDK's
 * processResultsForTests loop exactly: walk the variations accumulating each
 * `testRatio`, and if the visitor's 0..1 bucket falls under the running total,
 * that variation wins; the remaining share falls through to the parent.
 * Returns the winning variationId, or `parentId` for the parent share.
 */
export const pickVariantId = (visitorId: string, parentId: string, variations: BuilderVariationMap): string => {
  if (!visitorId || !variations) {
    return parentId;
  }
  const bucket = hashStringToUnit(`${visitorId}|${parentId}`);
  let cumulative = 0;
  for (const key of Object.keys(variations)) {
    const variation = variations[key];
    cumulative += variation.testRatio || 0;
    if (bucket < cumulative) {
      return variation.id || key;
    }
  }

  return parentId;
};

/**
 * Pin the `builder.tests.<contentId>` cookie the Builder SDK reads when it fires
 * an Insights impression/conversion. With `Builder.isStatic` (the SDK default)
 * `renderedVariantId` comes from THIS cookie, not the content's variationId, so
 * without it every impression logs against Default even though the visitor sees a
 * variant. We render the parent server-side, so this must be set from the client:
 * the blocking snippet sets it before Builder mounts on a hard load, and the hook
 * sets it during render (before the child mounts) so soft navigations attribute
 * correctly too. The value is the chosen variationId, or the parent/content id for
 * the parent share (Builder logs that as Default, which is correct for the control).
 */
export const pinBuilderTestCookie = (contentId?: string, variantId?: string): void => {
  if (typeof document === 'undefined' || !contentId || !variantId) {
    return;
  }
  document.cookie = `${TEST_COOKIE_PREFIX}.${contentId}=${variantId}; Path=/; Max-Age=${
    VISITOR_COOKIE_DAYS * 24 * 60 * 60
  }; SameSite=Lax`;
};
